// src/pages/SubcodonProfile/components/BatchJobStatus.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Alert,
} from "reactstrap";
import { fetchJobStatus } from "../../../helpers/eirna_backend_helper";
import { toast } from "react-toastify";

const BatchJobStatus = ({ jobStatus, onUpdateJobStatus, projectId }) => {
  const [jobResult, setJobResult] = useState(null);
  const [plotData, setPlotData] = useState(null);
  const [error, setError] = useState(null);

  const pollTaskStatus = useCallback(
    async (id) => {
      try {
        const response = await fetchJobStatus(id);
        const data = response?.data || response || {};

        if (["SUCCESS", "FAILURE"].includes(data.state)) {

          if (data.state === "SUCCESS") {
            if (data.result?.message === "user logged out") {
              return;
            }

            if (data.result?.message) {
              setError(data.result.message);
              toast.error(data.result.message);
              onUpdateJobStatus({
                ...jobStatus,
                status: "failed",
                message: data.result.message,
              });
            } else if (data.result) {
              setPlotData(data.result.plot || data.result);
              setJobResult(data.result);
              toast.success("Batch job completed successfully");
              onUpdateJobStatus({
                ...jobStatus,
                status: "completed",
                message: "Job completed successfully",
                result: data.result,
              });
            }
          } else {
            const errorMsg = `Job failed. Task ID: ${id}`;
            setError(errorMsg);
            toast.error(errorMsg);
            onUpdateJobStatus({
              ...jobStatus,
              status: "failed",
              message: errorMsg,
            });
          }
        } else {
          // Continue polling
          setTimeout(() => pollTaskStatus(id), 2000);
        }
      } catch (err) {
        console.error("Polling error:", err);
        const errorMsg = `Unable to fetch job status. Task ID: ${id}`;
        setError(errorMsg);
        toast.error(errorMsg);
        onUpdateJobStatus({
          ...jobStatus,
          status: "failed",
          message: errorMsg,
        });
      }
    },
    [jobStatus, onUpdateJobStatus]
  );

  useEffect(() => {
    if (jobStatus?.status === "running" && jobStatus.taskId) {;
      pollTaskStatus(jobStatus.taskId);
    }
  }, [jobStatus, pollTaskStatus]);

  const extractDownloadLink = (plotHtml) => {
    if (!plotHtml) return null;
    const parser = new DOMParser();
    const doc = parser.parseFromString(plotHtml, "text/html");
    const link = doc.querySelector("a[href]");
    if (link) {
      return {
        url: link.href,
        text: link.textContent.trim(),
      };
    }
    return null;
  };

  const handleDownload = (downloadUrl) => {
    window.open(downloadUrl, "_blank");
  };

  const getStatusColor = () => {
    switch (jobStatus?.status) {
      case "completed":
        return "success";
      case "failed":
        return "danger";
      case "running":
        return "info";
      default:
        return "info";
    }
  };

  const getStatusIcon = () => {
    switch (jobStatus?.status) {
      case "completed":
        return "ri-check-circle-line";
      case "failed":
        return "ri-error-warning-line";
      case "running":
        return "ri-loader-4-line";
      default:
        return "ri-information-line";
    }
  };

  if (!jobStatus) return null;

  const downloadLink = plotData ? extractDownloadLink(plotData) : null;

  return (
    <Card style={{ border: "1px solid #e5e7eb", borderRadius: "4px" }}>
      <CardHeader className="d-flex align-items-center">
        <i
          className={`${getStatusIcon()} me-2`}
          style={{ color: `var(--bs-${getStatusColor()})` }}
        ></i>
        <h5 className="mb-0">
          {jobStatus.status === "running" && "Processing Batch Job"}
          {jobStatus.status === "completed" && "Batch Job Completed"}
          {jobStatus.status === "failed" && "Batch Job Failed"}
        </h5>
      </CardHeader>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <i
              className={`${getStatusIcon()} me-2 ${
                jobStatus.status === "running" ? "rotate-animation" : ""
              }`}
              style={{
                fontSize: "18px",
                color:
                  jobStatus.status === "running"
                    ? "#3b82f6"
                    : jobStatus.status === "completed"
                    ? "#10b981"
                    : "#ef4444",
              }}
            ></i>
            <div>
              <div className="fw-semibold">
                {jobStatus.status === "running" &&
                  "Processing Batch Subcodon"}
                {jobStatus.status === "completed" &&
                  "Batch Subcodon Completed"}
                {jobStatus.status === "failed" && "Batch Subcodon Failed"}
              </div>
              <small className="text-muted">Task ID: {jobStatus.taskId}</small>
            </div>
          </div>
        </div>

        {jobStatus.status === "failed" && error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {jobStatus.status === "completed" && downloadLink && (
          <div className="text-center mb-3">
            <h6 className="text-success mb-2">Files Ready for Download</h6>
            <p className="text-muted mb-3">
              Your batch processing results are ready
            </p>
            <Button
              color="success"
              onClick={() => handleDownload(downloadLink.url)}
              className="d-flex align-items-center mx-auto"
              style={{ width: "fit-content" }}
            >
              <i className="ri-download-line me-2"></i>
              {downloadLink.text || "Download Files"}
            </Button>
          </div>
        )}

        {jobStatus.status === "completed" && jobResult?.short_code && (
          <Alert color="info" className="mb-3">
            <h6>Share this result using short code:</h6>
            <code>{jobResult.short_code}</code>
          </Alert>
        )}

        {jobStatus.status === "running" && (
          <Alert color="info" className="mb-0">
            Your batch subcodon job is being processed.
          </Alert>
        )}
      </CardBody>
    </Card>
  );
};

export default BatchJobStatus;
